<script module>
  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   */
</script>

<script>
  import MapComponent from "$lib/components/Map/Map.svelte";
  import DetailHeader from "$lib/components/DetailHeader.svelte";
  import { Block, BasicDropdown, Theme } from "@urbaninstitute/dataviz-components";
  import windowWidth from "$lib/stores/windowWidth.js";
  import { createConfig, setConfig } from "$lib/utils/config.js";
  import { getStatesInfo, getStateName } from "$utils/states.js";
  import { ToolData } from "$utils/data.js";
  import InfoBox from "./InfoBox.svelte";
  import InfoTable from "./InfoTable.svelte";
  import LineChart from "./Chart/LineChart.svelte";
  import { normalFormat, percentFormat } from "$utils/formats.js";
  import BarChartBlock from "./Chart/BarChartBlock.svelte";

  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   * @property { boolean } [hideDropdown = false]
   */

  /** @type {MapToolProps} */
  const {
    data,
    tableData,
    configData,
    mapType = "geo",
    showStateSummary,
    accentColor = "#fdbf11",
    featureOption = "states",
    dropdownLabel = "Select a metric to view",
    tablePlaceholderLabel = "Select a state or district to see data by county",
    hideDropdown = false
  } = $props();

  // use this effect to load data from the URLs passed as properties
  $effect(() => {
    // fetch primary data and store in $state
    if (!parsedData) {
      parsedData = new ToolData(data, tableData, configData);
    }
    // fetch config data and store in $state
    if (!parsedConfigData) {
      /** @satisfies { import("$utils/config").ColumnConfig[]} */
      parsedConfigData = createConfig(configData, accentColor);
    }
  });

  // hard coded values for this instance
  const MAP_COLUMNS = ["x2025_homelessness_per_10_000_persons"];
  const TOOLTIP_COLUMNS = ["x2025_homelessness_per_10_000_persons", "x2025_overall_homelessness"];

  /**
   * @param {Record<string, any>[]} allData
   * @param {string} map_id
   * @returns {Record<string, any>[]}
   */
  function getTableData(allData, map_id) {
    return allData.filter((d) => d.map_id === map_id);
  }

  let currentState = $state();
  let currentMetric = $state();
  // use effect to update current Metric to first data column if it isn't defined
  $effect(() => {
    if (parsedData && !currentMetric) {
      currentMetric = parsedData.getDataColumns()[0];
    }
  });

  /**
   * State to hold primary data
   * @type {ToolData | undefined}
   */
  let parsedData = $state();
  // state to hold configuration data
  let parsedConfigData = $state();
  // is the tool using the primary data for the table?
  let tableUsesMainData = $state(true);

  // set the config data into the context so child components can access it without prop drilling
  setConfig(() => parsedConfigData);

  // parse state summary into boolean
  const shouldShowStateSummary = $derived(showStateSummary === "true" && !tableUsesMainData);

  // derived state to hold the current map configuration, which is set based on the metric selection
  let currentMapConfig = $derived(
    currentMetric && parsedConfigData ? parsedConfigData.columns.get(currentMetric) : undefined
  );

  const stateDropdownData = getStatesInfo(featureOption).map(({ fips, state }) => ({
    value: fips,
    label: state
  }));

  const currentStateData = $derived(parsedData ? parsedData.getRow(currentState) : undefined);
  const currentStateVariables = $derived(getStateVariables(currentStateData));

  // build dropdown options from colNames
  const dataMetrics = $derived(
    parsedData && parsedConfigData
      ? parsedData
          .getDataColumns()
          .filter((k) => MAP_COLUMNS.includes(k)) // manual override
          .map((k) => ({ value: k, label: parsedConfigData.columns.get(k).column_label }))
      : []
  );

  let dropdownList = [
    { value: "overall_homelessness", label: "Overall" },
    {
      value: "people_experiencing_chronic_homelessness",
      label: "Chronic homelessness"
    },
    { value: "unsheltered_homelessness", label: "Unsheltered homelessness" },
    { value: "families_with_children", label: "Families with children" },
    { value: "individuals", label: "Individuals" },
    { value: "veterans", label: "Veterans" },
    { value: "unaccompanied_youth_under_25", label: "Unaccompanied youth (under 25 years old)" }
  ];
  let dropdownSelection = $state("overall_homelessness");

  let lineChartData = $derived(
    currentState
      ? Object.entries(currentStateData)
          .filter(([key, value]) => key.includes(dropdownSelection))
          .filter(([key, value]) => !key.includes("from"))
          .map(([metric, value]) => ({ year: +metric.substring(1, 5), value: value }))
      : {}
  );

  let bedsTableData = $derived(
    currentStateData
      ? [
          {
            "Housing program type": "Overall bed inventory",
            "Number of beds (2025)": normalFormat.format(
              currentStateData.x2025_overall_bed_inventory
            )
          },
          {
            "Housing program type": "Permanent supportive housing",
            "Number of beds (2025)": normalFormat.format(
              currentStateData.x2025_permanent_supportive_housing_inventory
            )
          },
          {
            "Housing program type": "Rapid rehousing",
            "Number of beds (2025)": normalFormat.format(
              currentStateData.x2025_rapid_rehousing_inventory
            )
          },
          {
            "Housing program type": "Transitional housing",
            "Number of beds (2025)": normalFormat.format(
              currentStateData.x2025_transitional_housing_inventory
            )
          },
          {
            "Housing program type": "Emergency shelter",
            "Number of beds (2025)": normalFormat.format(
              currentStateData.x2025_emergency_shelter_inventory
            )
          }
        ]
      : []
  );
  let bedsTableCols = ["Housing program type", "Number of beds (2025)"];

  let barChartData = $derived(
    currentState
      ? Object.entries(currentStateData)
          .filter(([key, value]) => key.includes("funding"))
          .map(([key, value]) => ({
            category: key.includes("overall")
              ? "All"
              : key.includes("rapid")
                ? "Rapid rehousing"
                : "Permanent supportive housing",
            label: key.includes("federal")
              ? "Any federal funding"
              : key.includes("va")
                ? "Veterans Affairs"
                : "McKinney-Vento",
            value: value
          }))
      : []
  );

  /**
   * @param {CustomEvent<{props: {fips: string}}>} e
   */
  function handleStateSelect(e) {
    if (!e) {
      currentState = null;
      return;
    }
    if (e.detail) {
      const { fips, map_id } = e.detail.props;
      currentState = fips || map_id;
    } else {
      currentState = null;
    }
  }

  /**
   * @param {string} metric
   * @param {number} maxWidth
   */
  function getDropdownWidth(metric, maxWidth) {
    return Math.min(220, maxWidth);
  }
  function getStateVariables(stateData) {
    if (!parsedConfigData) {
      return undefined;
    }
    let result = {
      state: getStateName(stateData.map_id)
    };
    for (let key in stateData) {
      let colConfig = parsedConfigData.columns.get(key);
      if (colConfig) {
        result[key] = colConfig.format(stateData[key]);
      } else {
        result[key] = stateData[key];
      }
    }
    return result;
  }

  /**
   * @param {number} metric
   * @returns {string}
   */
  function addOrdinal(metric) {
    if (metric % 10 == 1 && metric != 11) return metric + "st";
    if (metric % 10 == 2 && metric != 12) return metric + "nd";
    if (metric % 10 == 3 && metric != 13) return metric + "rd";
    return metric + "th";
  }
</script>

<svelte:window bind:innerWidth={$windowWidth} />
<Theme>
  <Block width="body">
    <h4 class="tool-title">
      Number of People Experiencing Homelessness per 10,000 Residents, 2025
    </h4>
  </Block>
  <Block width="wide">
    {#if parsedData && parsedConfigData}
      {#if !hideDropdown}
        <div class="tool-header" style={`--accent-color: ${accentColor};`}>
          {dropdownLabel}
          <BasicDropdown
            data={dataMetrics}
            variant="secondary-yellow"
            inlineLabel="Select a metric to view"
            id="metric-select"
            placeholder={null}
            on:change={(e) => {
              // logClickToGA(e.target, "metric-select--" + e.target.value);
            }}
            bind:value={currentMetric}
          />
        </div>
      {/if}
      {#if currentMapConfig}
        <div class="map-container">
          <MapComponent
            data={parsedData.data}
            metric={currentMetric}
            onStateSelect={handleStateSelect}
            colorPalette={currentMapConfig.scale_color}
            scaleBreaks={currentMapConfig.scale_breaks}
            scaleType={currentMapConfig.scale_type}
            minMax={currentMapConfig.scale_min_max}
            {featureOption}
            {mapType}
            bind:currentState
            tooltipColumns={TOOLTIP_COLUMNS}
          />
        </div>
      {/if}

      <DetailHeader bind:currentState {stateDropdownData} label={tablePlaceholderLabel} />

      <!-- <Table data={currentTableData} cols={tableColNames} labelColumn={!tableUsesMainData} /> -->

      <div class="box">
        <InfoBox>
          <h4>
            How has homelessness changed{currentState ? " in " : ""}<span class="state-name"
              >{currentState ? getStateName(currentState) : ""}</span
            > in the past 10 years?
          </h4>
          {#if currentState}
            <p>
              In <span class="state-name">{getStateName(currentState)}</span>,
              <strong>{normalFormat.format(currentStateData.x2025_overall_homelessness)}</strong>
              people experienced homelessness on any given night in 2025, {currentStateData.x2025_increase_or_decrease_in_overall_homelessness_from_2024}
              of
              <strong
                >{percentFormat
                  .format(
                    Math.abs(
                      currentStateData.x2025_percent_change_in_overall_homelessness_from_2024
                    )
                  )
                  .replace("%", " percent")}</strong
              > from 2024. Below, explore homelessness trends over the past 10 years and trends among
              specific groups.
            </p>
            <p>
              <i class="chart-header">
                Number of people experiencing homelessness between 2016 and 2025
              </i>
            </p>
            <BasicDropdown
              data={dropdownList}
              bind:value={dropdownSelection}
              id="line-chart-selector"
              inlineLabel="Pick a metric to show on the line chart."
            />

            {#key currentState + dropdownSelection}
              <LineChart
                data={lineChartData}
                {currentState}
                tooltipLabel={dropdownList.find((d) => d.value == dropdownSelection).label}
              />
            {/key}
            <p class="source-and-notes first">
              <strong>Source:</strong> US Department of Housing and Urban Development 2007–25 Point-in-Time
              Count estimates by state.
            </p>
            <p class="source-and-notes">
              <strong>Notes:</strong> Point-in-Time Counts occur in January each year. Because of the
              COVID-19 pandemic, the count was not conducted in 2021.
            </p>
          {/if}
        </InfoBox>
      </div>
      <div class="infobox-container">
        <div class="first-column">
          <InfoBox>
            <h4>
              How many beds does{currentState ? " in " : ""}<span class="state-name"
                >{currentState ? getStateName(currentState) : ""}</span
              > have?
            </h4>
            {#if currentState}
              <p>
                In 2025, <span class="state-name"
                  >{currentState ? getStateName(currentState) : ""}</span
                >
                had
                <strong>{normalFormat.format(currentStateData.x2025_overall_bed_inventory)}</strong>
                total beds available for people experiencing homelessness.
              </p>
              <InfoTable data={bedsTableData} cols={bedsTableCols} />
              <p class="source-and-notes first">
                <strong>Source:</strong> US Department of Housing and Urban Development 2025 Housing
                Inventory Count by state.
              </p>
              <p class="source-and-notes">
                <strong>Notes:</strong> Data include beds in housing programs in 2025 that were available
                for use as well as those still under development. These data may undercount total beds
                depending on the comprehensiveness of reporting in each state and locality.
              </p>
            {/if}
          </InfoBox>
        </div>
        <div class="second-column">
          <InfoBox>
            <h4>
              How many beds{currentState ? " in " : ""}<span class="state-name"
                >{currentState ? getStateName(currentState) : ""}</span
              > rely on federal funding?
            </h4>
            {#if currentState}
              <p>
                In 2025, <strong
                  >{percentFormat
                    .format(
                      currentStateData.x2025_percent_of_overall_bed_inventory_that_relies_on_any_federal_funding_source
                    )
                    .replace("%", " percent")}</strong
                >
                of the overall housing bed inventory in
                <span class="state-name">{currentState ? getStateName(currentState) : ""}</span> relied
                on federal funding. Below, explore the share of beds that rely on funding through the
                McKinney-Vento Act and US Department of Veterans Affairs programs.
              </p>
              <p>
                <i class="chart-header"
                  >Share of beds available to people experiencing homelessness that rely on federal
                  funding</i
                >
              </p>
              <BarChartBlock data={barChartData} />
              <p class="source-and-notes first">
                <strong>Source:</strong> US Department of Housing and Urban Development 2025 Housing
                Inventory Count by state.
              </p>
              <p class="source-and-notes">
                <strong>Notes:</strong> Data include beds in housing programs in 2025 that were available
                for use as well as those still under development. These data may undercount total beds
                depending on the comprehensiveness of reporting in each state and locality.
              </p>
            {/if}
          </InfoBox>
        </div>
      </div>
    {/if}
  </Block>
</Theme>

<style>
  h4.tool-title {
    margin-bottom: var(--spacing-12) !important;
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--spacing-8);
    text-align: center;
  }
  .tool-header :global(.dropdown-parent) {
    display: inline-block;
  }
  .tool-header :global(.dropdown-container .icons svg) {
    display: block;
  }
  .tool-header :global(.dropdown-container select.dropdown-select) {
    background-color: var(--accent-color);
    border: var(--accent-color);
  }
  .tool-header :global(select) {
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header :global(select option) {
    font-size: var(--font-size-normal) !important;
  }
  .map-container {
    max-width: 960px;
    margin: 0 auto 26px;
  }
  .state-name {
    border-bottom: solid var(--color-yellow) 4px;
    /* text-underline-offset: 4px; */
  }
  h5.chart-header {
    margin-bottom: 10px;
  }
  .infobox-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-2);
    .second-column {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-2);
    }
    @media screen and (max-width: 1020px) {
      grid-template-columns: 1fr;
    }
  }
  .box {
    margin-bottom: var(--spacing-2);
  }
  .infobox-container p.source-and-notes,
  .box p.source-and-notes {
    font-size: var(--font-size-small);
    line-height: var(--line-height-snug);
    margin: 10px 0 0 0;
    &.first {
      margin-top: 20px;
    }
  }
  .infobox-container h4,
  .box h4 {
    margin-top: 0;
  }
  @media (max-width: 540px) {
    .tool-header :global(select) {
      font-size: var(--font-size-xl) !important;
    }
    .tool-header {
      font-size: var(--font-size-xl) !important;
    }
  }
</style>
